import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_MARKETPLACEACCOUNT_API ?? '/api/v1';

export type MarketplaceCode = 'amazon_spapi' | 'flipkart' | 'myntra' | 'ajio' | 'shopify' | 'woocommerce';

export type MarketplaceAccount = {
  id: string;
  account_code: string;
  marketplace: MarketplaceCode;
  display_name: string;
  seller_id?: string;
  merchant_id?: string;
  region?: string;
  marketplace_ids?: string[];
  status: string;
  connection_status: string;
  created_at: string;
  updated_at: string;
};

export type ConnectionHealth = {
  account_id: string;
  connection_status: string;
  token_status: string;
  credential_status: string;
  api_available: boolean;
  sync_healthy: boolean;
  score: number;
  message?: string;
  last_checked_at: string;
};

export type MarketplaceCapability = {
  id: string;
  capability_type: string;
  enabled: boolean;
  marketplace: MarketplaceCode;
};

export type AccountDashboard = {
  total_accounts: number;
  active_accounts: number;
  healthy_accounts: number;
  expiring_tokens: number;
  by_marketplace: Record<string, number>;
};

export type ChannelConfiguration = {
  id: string;
  account_id: string;
  default_warehouse_id?: string;
  default_currency?: string;
  default_price_book_id?: string;
  default_inventory_rule?: string;
  default_sync_rule?: string;
  feed_settings?: Record<string, unknown>;
  retry_settings?: { max_retries: number; initial_delay_ms: number; max_delay_ms: number; backoff_factor: number };
};

export type TestConnectionResult = {
  success: boolean;
  connection_status: string;
  token_status: string;
  message?: string;
  latency_ms: number;
};

export type InitOAuthResponse = {
  session_id: string;
  authorization_url: string;
  state: string;
  expires_at: string;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const marketplaceaccountApi = {
  dashboard: () => request<AccountDashboard>('/marketplace/accounts/dashboard'),
  list: (params?: { marketplace?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.marketplace) q.set('marketplace', params.marketplace);
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    return request<MarketplaceAccount[]>(`/marketplace/accounts${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<MarketplaceAccount>(`/marketplace/accounts/${id}`),
  create: (body: {
    account_code: string;
    marketplace: MarketplaceCode;
    display_name: string;
    region?: string;
    seller_id?: string;
    merchant_id?: string;
    marketplace_ids?: string[];
  }) => request<MarketplaceAccount>('/marketplace/accounts', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Record<string, unknown>) =>
    request<MarketplaceAccount>(`/marketplace/accounts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => request<void>(`/marketplace/accounts/${id}`, { method: 'DELETE' }),
  connect: (id: string, body: Record<string, unknown>) =>
    request<MarketplaceAccount>(`/marketplace/accounts/${id}/connect`, { method: 'POST', body: JSON.stringify(body) }),
  disconnect: (id: string) =>
    request<MarketplaceAccount>(`/marketplace/accounts/${id}/disconnect`, { method: 'POST' }),
  verify: (id: string) => request<TestConnectionResult>(`/marketplace/accounts/${id}/verify`, { method: 'POST' }),
  test: (id: string) => request<TestConnectionResult>(`/marketplace/accounts/${id}/test`, { method: 'POST' }),
  refreshToken: (id: string) =>
    request<{ id: string; status: string; expires_at: string }>(`/marketplace/accounts/${id}/refresh-token`, { method: 'POST' }),
  health: (id: string) => request<ConnectionHealth>(`/marketplace/accounts/${id}/health`),
  capabilities: (id: string) => request<MarketplaceCapability[]>(`/marketplace/accounts/${id}/capabilities`),
  initOAuth: (id: string, body: { redirect_uri: string; client_id: string }) =>
    request<InitOAuthResponse>(`/marketplace/accounts/${id}/oauth/init`, { method: 'POST', body: JSON.stringify(body) }),
  credentials: (id: string) => request<Record<string, string>>(`/marketplace/accounts/${id}/credentials`),
  getConfig: (id: string) => request<ChannelConfiguration>(`/marketplace/accounts/${id}/config`),
  updateConfig: (id: string, body: ChannelConfiguration) =>
    request<ChannelConfiguration>(`/marketplace/accounts/${id}/config`, { method: 'PUT', body: JSON.stringify(body) }),
  clone: (id: string, account_code: string) =>
    request<MarketplaceAccount>(`/marketplace/accounts/${id}/clone`, { method: 'POST', body: JSON.stringify({ account_code }) }),
};
